import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { feedbackAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import {
  Bug,
  Lightbulb,
  MessageSquare,
  Send,
  Loader2,
  CheckCircle,
  AlertCircle,
  Upload,
  X,
  Shield
} from 'lucide-react';
import { toast } from 'sonner';

const CATEGORIES = [
  {
    value: 'bug',
    label: 'Report a Bug',
    icon: Bug,
    color: 'text-red-600 bg-red-100',
    description: 'Something isn\'t working correctly'
  },
  {
    value: 'feature',
    label: 'Request a Feature',
    icon: Lightbulb,
    color: 'text-amber-600 bg-amber-100',
    description: 'Suggest a new feature or improvement'
  },
  {
    value: 'feedback',
    label: 'General Feedback',
    icon: MessageSquare,
    color: 'text-blue-600 bg-blue-100',
    description: 'Share your thoughts or contact us'
  }
];

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const ALLOWED_FILE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'application/pdf'];

export default function ContactFeedback() {
  const { user, isAuthenticated } = useAuth();
  const [formData, setFormData] = useState({
    name: user?.full_name || '',
    email: user?.email || '',
    category: 'feedback',
    subject: '',
    message: ''
  });
  const [attachment, setAttachment] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (!formData.subject.trim()) {
      newErrors.subject = 'Subject is required';
    }
    
    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    } else if (formData.message.trim().length < 10) {
      newErrors.message = 'Message must be at least 10 characters';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (file.size > MAX_FILE_SIZE) {
      toast.error('File size must be less than 5MB');
      return;
    }
    
    if (!ALLOWED_FILE_TYPES.includes(file.type)) {
      toast.error('Only JPG, PNG, GIF, and PDF files are allowed');
      return;
    }
    
    setAttachment(file);
  };

  const removeAttachment = () => {
    setAttachment(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please fix the errors in the form');
      return;
    }
    
    setLoading(true);
    
    try {
      const submitData = {
        ...formData,
        page_url: window.location.href,
        user_agent: navigator.userAgent
      };
      
      // If there's an attachment, convert to base64
      if (attachment) {
        const reader = new FileReader();
        const base64 = await new Promise((resolve) => {
          reader.onload = () => resolve(reader.result);
          reader.readAsDataURL(attachment);
        });
        submitData.attachment = {
          name: attachment.name,
          type: attachment.type,
          size: attachment.size,
          data: base64
        };
      }
      
      await feedbackAPI.submit(submitData);
      setSubmitted(true);
      toast.success('Thank you! Your feedback has been submitted.');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to submit feedback. Please try again.';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFormData({
      name: user?.full_name || '',
      email: user?.email || '',
      category: 'feedback',
      subject: '',
      message: ''
    });
    setAttachment(null);
    setSubmitted(false);
    setErrors({});
  };

  const getCategoryPlaceholder = () => {
    switch (formData.category) {
      case 'bug':
        return 'Please describe the bug in detail. Include steps to reproduce if possible...';
      case 'feature':
        return 'Describe the feature you\'d like to see. Why would it be useful?';
      default:
        return 'Share your thoughts, questions, or feedback...';
    }
  };

  const getSubjectPlaceholder = () => {
    switch (formData.category) {
      case 'bug':
        return 'e.g., Tournament calendar not loading correctly';
      case 'feature':
        return 'e.g., Add ability to export tournament schedule to PDF';
      default:
        return 'e.g., Question about notification settings';
    }
  };

  if (submitted) {
    return (
      <div className="max-w-2xl mx-auto" data-testid="feedback-page">
        <Card className="card-base">
          <CardContent className="py-12 text-center">
            <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-emerald-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-3">Thank You!</h2>
            <p className="text-slate-600 mb-6 max-w-md mx-auto">
              Your {formData.category === 'bug' ? 'bug report' : formData.category === 'feature' ? 'feature request' : 'feedback'} has been submitted successfully. 
              We'll review it and get back to you at <strong>{formData.email}</strong>.
            </p>
            <Button onClick={handleReset} variant="outline" data-testid="submit-another-btn">
              Submit Another
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6 animate-fade-in" data-testid="feedback-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900">Contact & Feedback</h1>
        <p className="text-slate-600 mt-1">
          We'd love to hear from you. Report bugs, request features, or share your thoughts.
        </p>
      </div>

      {/* Category Selection */}
      <Card className="card-base">
        <CardHeader>
          <CardTitle className="text-lg">What would you like to do?</CardTitle>
        </CardHeader>
        <CardContent>
          <RadioGroup
            value={formData.category}
            onValueChange={(value) => setFormData({ ...formData, category: value })}
            className="grid grid-cols-1 md:grid-cols-3 gap-4"
          >
            {CATEGORIES.map((cat) => {
              const Icon = cat.icon;
              const isSelected = formData.category === cat.value;
              return (
                <div key={cat.value}>
                  <RadioGroupItem
                    value={cat.value}
                    id={cat.value}
                    className="peer sr-only"
                  />
                  <Label
                    htmlFor={cat.value}
                    className={`flex flex-col items-center gap-3 p-6 rounded-xl border-2 cursor-pointer transition-all ${
                      isSelected
                        ? 'border-primary bg-primary/5'
                        : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                    }`}
                    data-testid={`category-${cat.value}`}
                  >
                    <div className={`w-14 h-14 rounded-full flex items-center justify-center ${cat.color}`}>
                      <Icon className="w-7 h-7" />
                    </div>
                    <div className="text-center">
                      <p className="font-semibold text-slate-900">{cat.label}</p>
                      <p className="text-xs text-slate-500 mt-1">{cat.description}</p>
                    </div>
                  </Label>
                </div>
              );
            })}
          </RadioGroup>
        </CardContent>
      </Card>

      {/* Feedback Form */}
      <Card className="card-base">
        <CardHeader>
          <CardTitle className="text-lg">Your Message</CardTitle>
          <CardDescription>
            Fill out the form below and we'll get back to you as soon as possible.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Name & Email Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Your name"
                  className={errors.name ? 'border-red-500' : ''}
                  data-testid="feedback-name"
                />
                {errors.name && (
                  <p className="text-xs text-red-500 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {errors.name}
                  </p>
                )}
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="your@email.com"
                  className={errors.email ? 'border-red-500' : ''}
                  data-testid="feedback-email"
                />
                {errors.email && (
                  <p className="text-xs text-red-500 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {errors.email}
                  </p>
                )}
              </div>
            </div>

            {/* Subject */}
            <div className="space-y-2">
              <Label htmlFor="subject">Subject *</Label>
              <Input
                id="subject"
                value={formData.subject}
                onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                placeholder={getSubjectPlaceholder()}
                className={errors.subject ? 'border-red-500' : ''}
                data-testid="feedback-subject"
              />
              {errors.subject && (
                <p className="text-xs text-red-500 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {errors.subject}
                </p>
              )}
            </div>

            {/* Message */}
            <div className="space-y-2">
              <Label htmlFor="message">Message *</Label>
              <Textarea
                id="message"
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                placeholder={getCategoryPlaceholder()}
                rows={6}
                className={`resize-none ${errors.message ? 'border-red-500' : ''}`}
                data-testid="feedback-message"
              />
              {errors.message && (
                <p className="text-xs text-red-500 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {errors.message}
                </p>
              )}
              <p className="text-xs text-slate-400 text-right">
                {formData.message.length} characters
              </p>
            </div>

            {/* File Attachment */}
            <div className="space-y-2">
              <Label>Attachment (optional)</Label>
              {attachment ? (
                <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Upload className="w-5 h-5 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate">{attachment.name}</p>
                    <p className="text-xs text-slate-500">
                      {(attachment.size / 1024).toFixed(1)} KB
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={removeAttachment}
                    className="text-slate-400 hover:text-red-500"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <div className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center hover:border-slate-300 transition-colors">
                  <input
                    type="file"
                    id="attachment"
                    onChange={handleFileChange}
                    accept=".jpg,.jpeg,.png,.gif,.pdf"
                    className="hidden"
                  />
                  <Label
                    htmlFor="attachment"
                    className="cursor-pointer"
                  >
                    <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                    <p className="text-sm text-slate-600">
                      Click to upload a screenshot or file
                    </p>
                    <p className="text-xs text-slate-400 mt-1">
                      JPG, PNG, GIF, PDF up to 5MB
                    </p>
                  </Label>
                </div>
              )}
            </div>

            {/* Privacy Notice */}
            <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-lg border border-slate-200">
              <Shield className="w-5 h-5 text-slate-500 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-slate-600">
                <strong>Privacy Notice:</strong> We only use your information to respond to your inquiry. 
                Your data is stored securely and will not be shared with third parties.
              </p>
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              className="w-full btn-primary"
              disabled={loading}
              data-testid="feedback-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Submit {formData.category === 'bug' ? 'Bug Report' : formData.category === 'feature' ? 'Feature Request' : 'Feedback'}
                </>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
